import type { Player, Team } from "../../pages/logger/types";
import type { TeamPalette } from "../../lib/teamPalette";
import { formatDisplayName } from "../../lib/nameFormat";
import {
  resolveRosterRowState,
  isOnPitch,
  type PublicRosterStatusIndex,
  type PlayerMatchStatus,
  type RosterRowState,
} from "./publicRosterStatus";

/**
 * PublicTeamSheet — one team's matchday sheet for the public share page
 * (PMA-COCK-125; supersedes `PublicRosterColumn`).
 *
 * Two things the client asked for, in their own words:
 *  - "no se puede 1 columna titulares, y otra suplentes?" — starters and
 *    substitutes are now two named, side-by-side columns rather than one
 *    reflowing 2-up grid that split each group across both tracks.
 *  - "de hecho que se le puede poner colores, igual a los cambios, verdes
 *    y en ese caso pondria amarillos o rojos" — each row carries the
 *    player's live standing in the same colour language the timeline below
 *    already uses: emerald for coming on, red for going off, yellow/red
 *    chips for cards. Reusing that language (rather than inventing a
 *    second one) is what lets a viewer move between the two surfaces
 *    without relearning anything.
 *
 * Colour is doing two separate jobs here, so it is split across two
 * channels that can't be confused: the TEAM identity is a flat tint (the
 * jersey chip's background and the header rail, both from the team's own
 * palette), while the player's STATUS is always a glyph — a triangle or a
 * card chip. A viewer never has to ask whether a green thing means "this
 * team plays in green" or "this player came on".
 *
 * Names are set in Barlow Condensed. That is the matchday-teamsheet /
 * shirt-nameplate idiom, and it buys roughly four more characters per row
 * at the same size — which is what makes a genuine two-column split
 * survive a 390px phone instead of truncating half the squad.
 *
 * Responsive behaviour is CSS-only (no resize listener, no duplicated DOM
 * — the previous `inline`/`stacked` variant pair rendered both copies and
 * hid one, which is what put two crests for the same team inside
 * `public-score-bar` and broke a strict-mode Playwright locator). The
 * starters/substitutes split holds at every width; only the panel's own
 * placement changes, and that is decided by the caller
 * (`PublicMatchAnalytics.tsx`), not here.
 *
 * `mirrored` flips the away sheet at `xl` only, where the two sheets flank
 * the centre plaque and the mirroring reinforces the header's home-vs-away
 * symmetry. Below `xl` the sheets stack or sit side by side in normal
 * reading order, where mirroring would just make the away team harder to
 * scan.
 */

interface PublicTeamSheetProps {
  team: Team;
  side: "home" | "away";
  palette: TeamPalette;
  /** Per-player status for THIS team — see `buildRosterStatusIndex`. */
  status: PublicRosterStatusIndex;
  /** Mirror the layout at `xl` and up. Set for the away side. */
  mirrored?: boolean;
  t: (key: string, fallback?: string) => string;
}

const sortByJersey = (players: Player[]): Player[] =>
  [...players].sort((a, b) => (a.jersey_number || 0) - (b.jersey_number || 0));

/**
 * Team palettes are hex today (both the hashed defaults and user-supplied
 * `primary_color`), but this renders untrusted-ish team data, so widen the
 * hex to 8 digits only when it really is a 6-digit hex — anything else
 * falls back to a neutral tint rather than emitting invalid CSS that would
 * drop the chip's background entirely.
 */
const withAlpha = (hex: string, alphaHex: string): string =>
  /^#[0-9a-fA-F]{6}$/.test(hex)
    ? `${hex}${alphaHex}`
    : "rgba(148,163,184,0.18)";

/**
 * Name colour per row state. Deliberately never `text-slate-500`: that is
 * 3.07:1 on this card's #1e293b and fails AA — it has shipped as a defect
 * on this page twice. A dimmed row uses `text-slate-400` (4.6:1).
 */
const NAME_CLASS_BY_STATE: Record<RosterRowState, string> = {
  sentOff: "text-slate-400 line-through decoration-red-500/70",
  off: "text-slate-400",
  on: "text-emerald-300",
  default: "text-slate-100",
};

function CardChips({
  status,
  t,
}: {
  status: PlayerMatchStatus;
  t: PublicTeamSheetProps["t"];
}) {
  if (!status.yellowCards && !status.sentOff) return null;
  return (
    <span className="flex shrink-0 items-center gap-[2px]">
      {Array.from({ length: status.yellowCards }, (_, i) => (
        <span
          key={`y${i}`}
          className="inline-block h-[9px] w-[6px] rounded-[1px] bg-yellow-400"
          data-testid="public-team-sheet-yellow-chip"
        />
      ))}
      {status.sentOff && (
        <span
          className="inline-block h-[9px] w-[6px] rounded-[1px] bg-red-500"
          data-testid="public-team-sheet-red-chip"
        />
      )}
      <span className="sr-only">
        {status.sentOff
          ? t("publicSheetSentOff", "Sent off")
          : t("publicSheetBooked", "Booked")}
      </span>
    </span>
  );
}

/**
 * `t`'s prop type is deliberately narrow (`(key, fallback?) => string`, no
 * interpolation options — see `PublicTeamSheetProps.t`), matching the type
 * `PublicMatchAnalytics.tsx` casts its real `useTranslation` result down to
 * for every sibling `match/` component. Rather than widen that shared type
 * just for this one count, `playerGoalsScored`'s `{{count}}` token is
 * substituted by hand after the lookup — the exact `interpolateFallback`
 * pattern `PublicEventToast.tsx` already established for the same
 * constraint, safe whether `t` returns the real translation (which never
 * received interpolation data, so the token survives untouched) or the bare
 * fallback string (uninitialized-i18next test environments).
 */
const interpolateCount = (text: string, count: number): string =>
  text.split("{{count}}").join(String(count));

/**
 * Same hand-substitution pattern as `interpolateCount`, for the
 * `publicRosterGoalsBreakdown` key's two tokens (PMA-COCK-195).
 */
const interpolateBreakdown = (
  text: string,
  goals: number,
  penalties: number,
): string =>
  text
    .split("{{goals}}")
    .join(String(goals))
    .split("{{penalties}}")
    .join(String(penalties));

/**
 * PMA-COCK-145/148 — goal-ball indicator, mirroring PMA-COCK-145's identical
 * mark on the cockpit's "En cancha" list (`PlayerSelectorPanel.tsx`).
 *
 * **Dated reversal (2026-09-24).** The original PMA-COCK-148 shipped a
 * bare-count multiplier (`⚽ ×3`) — the client now asks for one ball PER
 * goal instead, in the order scored ("solo mostrar la bola por cada gol"),
 * with the multiplier gone entirely. `status.goals` is now an ORDERED list
 * (`publicRosterStatus.ts`'s `PlayerGoalMark[]`), one entry per ball; this
 * renders exactly that many `⚽` glyphs, each carrying its own "(P)" mark
 * when `kind === "penalty"` rather than one aggregate mark for the whole
 * row — a brace with one penalty now reads as two distinct balls, only one
 * of which is tagged, not "⚽×2" plus a single detached "(P)".
 *
 * Reuses the cockpit's `playerGoalsScored`/`publicRosterGoalsBreakdown` i18n
 * keys for the accessible name (unchanged wording — only the visual ball
 * count/order changed, not the phrasing), same as before this reversal.
 */
function GoalMark({
  status,
  t,
}: {
  status: PlayerMatchStatus;
  t: PublicTeamSheetProps["t"];
}) {
  const goals = status.goals;
  if (goals.length === 0) return null;
  const penalties = goals.filter((g) => g.kind === "penalty").length;
  const label =
    penalties > 0
      ? interpolateBreakdown(
          t(
            "publicRosterGoalsBreakdown",
            "{{goals}} goals, {{penalties}} from the penalty spot",
          ),
          goals.length,
          penalties,
        )
      : interpolateCount(
          t("playerGoalsScored", "{{count}} goal(s) scored"),
          goals.length,
        );
  return (
    <span
      data-testid="public-team-sheet-goal-mark"
      role="img"
      aria-label={label}
      title={label}
      className="flex shrink-0 items-center gap-0.5 text-[10px] font-bold tabular-nums text-slate-100"
    >
      {goals.map((goal, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-px"
          aria-hidden="true"
        >
          <span>⚽</span>
          {goal.kind === "penalty" && (
            <span data-testid="public-team-sheet-goal-penalty-mark">(P)</span>
          )}
        </span>
      ))}
    </span>
  );
}

/**
 * PMA-COCK-145/148 dated reversal (2026-09-24) — "AG" chip for an own goal,
 * shown on the SCORER'S OWN row (`publicRosterStatus.ts`'s `ownGoals`
 * attributes it there by roster membership, not by which team the event
 * credits on the scoreboard). Deliberately NOT one of `GoalMark`'s balls —
 * the client asked for the own goal to be visible "como cualquier otro
 * gol", but it is not a goal FOR this player's team, so it gets its own
 * chip rather than joining the ordered ball list.
 *
 * **Dated addendum (2026-09-25, client wave, "Ponerle la bolita aquí a la
 * par de AG").** The chip alone (with the count folded only into the
 * accessible name) wasn't enough — the client wants a visible ball beside
 * it, one per own goal, mirroring `GoalMark`'s "one ball per goal" idiom
 * exactly, just parked beside the "AG" chip instead of inside the ordered
 * goal-ball list (still not one of `GoalMark`'s own balls — an own goal is
 * not a goal FOR this player's team).
 *
 * **ui-judge round-1 defect 5 (P2):** the own-goal balls reused the exact
 * same bare `⚽` glyph as `GoalMark`'s scored-goal balls, told apart only by
 * the row's own uniform `gap-1.5` and the trailing "AG" chip — a row with
 * both marks read as "goal, own goal, own goal" (three goals) at a glance.
 * Each own-goal ball now carries a `ring-1 ring-rose-400` (same accent
 * family as the radial wheel's own Autogol wedge outline and the goal-mouth
 * map's own own-goal ring), and this whole mark gets its own `ml-0.5` on
 * top of the row's `gap-1.5` — a `gap-2`-equivalent (8px) break from
 * `GoalMark`'s group, wider than the uniform inter-element spacing
 * elsewhere in the row.
 */
function OwnGoalMark({
  status,
  t,
}: {
  status: PlayerMatchStatus;
  t: PublicTeamSheetProps["t"];
}) {
  if (status.ownGoals <= 0) return null;
  const label = interpolateCount(
    t("publicRosterOwnGoalMark", "{{count}} own goal(s)"),
    status.ownGoals,
  );
  return (
    <span
      data-testid="public-team-sheet-own-goal-mark"
      role="img"
      aria-label={label}
      title={label}
      className="ml-0.5 flex shrink-0 items-center gap-1"
    >
      <span
        aria-hidden="true"
        className="flex items-center gap-1 text-[10px] font-bold tabular-nums text-slate-100"
      >
        {Array.from({ length: status.ownGoals }, (_, i) => (
          <span
            key={i}
            className="flex h-3.5 w-3.5 items-center justify-center rounded-full ring-1 ring-rose-400"
          >
            ⚽
          </span>
        ))}
      </span>
      <span
        aria-hidden="true"
        className="flex shrink-0 items-center rounded-[3px] bg-orange-500/20 px-1 text-[9px] font-bold uppercase tracking-wide text-orange-300"
      >
        AG
      </span>
    </span>
  );
}

function SubstitutionMark({
  status,
  t,
}: {
  status: PlayerMatchStatus;
  t: PublicTeamSheetProps["t"];
}) {
  // A player who came on and was later taken off shows the exit, the more
  // recent fact — one mark per row keeps the column scannable.
  if (status.wentOffMinute) {
    return (
      <span
        className="flex shrink-0 items-center gap-[2px] whitespace-nowrap text-[9px] font-semibold tabular-nums text-red-400"
        data-testid="public-team-sheet-mark-off"
      >
        <span aria-hidden="true">▼</span>
        <span className="sr-only">
          {t("publicSheetCameOff", "Substituted off")}
        </span>
        {status.wentOffMinute}
      </span>
    );
  }
  if (status.cameOnMinute) {
    return (
      <span
        className="flex shrink-0 items-center gap-[2px] whitespace-nowrap text-[9px] font-semibold tabular-nums text-emerald-400"
        data-testid="public-team-sheet-mark-on"
      >
        <span aria-hidden="true">▲</span>
        <span className="sr-only">
          {t("publicSheetCameOn", "Substituted on")}
        </span>
        {status.cameOnMinute}
      </span>
    );
  }
  return null;
}

export default function PublicTeamSheet({
  team,
  side,
  palette,
  status,
  mirrored = false,
  t,
}: PublicTeamSheetProps) {
  const players = team.players ?? [];
  // Split by who is on the pitch RIGHT NOW (`isOnPitch`), not by the static
  // `is_starter` flag (PMA-COCK-127 v1.118.0, 2026-08-24 — reverses the
  // SAME-DAY v1.117.0 change, which had switched to the static split so the
  // "Titulares"/"Suplentes" headings would be literally true). The client's
  // ORIGINAL ask was for a substitution to move both players across
  // ("que cuando uno haga cambios los jugadores se muevan de un lado a otro
  // aqui?"); v1.117.0 traded that away for truthful static labels, and the
  // client then chose to reverse it — live columns with labels that describe
  // them honestly ("En cancha"/"Banca") rather than static columns under
  // "Titulares"/"Suplentes". See PMA-COCK-127's contract entry for the full
  // history and PMA-COCK-135 (reinstated) for `isOnPitch`'s own rules —
  // notably that a dismissal moves no one.
  const onPitch = sortByJersey(
    players.filter((p) => isOnPitch(status.get(p.id), Boolean(p.is_starter))),
  );
  const bench = sortByJersey(
    players.filter((p) => !isOnPitch(status.get(p.id), Boolean(p.is_starter))),
  );

  if (!onPitch.length && !bench.length) return null;

  const alignItems = mirrored ? "xl:items-end" : "";
  const textAlign = mirrored ? "xl:text-right" : "";

  const renderGroup = (
    labelKey: string,
    fallback: string,
    list: Player[],
    groupKey: "starters" | "substitutes",
  ) => {
    if (!list.length) return null;
    return (
      <div
        className={`flex min-w-0 flex-1 flex-col gap-1 ${alignItems}`}
        data-testid={`public-team-sheet-${side}-${groupKey}`}
      >
        {/* Label then count, never reversed. The mirrored side only changes
            which edge the pair sits against (`justify-end`); flipping the
            order too made the away columns read "5 SUPLENTES". */}
        <div
          className={`flex w-full items-baseline gap-1.5 ${mirrored ? "xl:justify-end" : ""} ${textAlign}`}
        >
          <span className="truncate font-condensed text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {t(labelKey, fallback)}
          </span>
          <span
            className="shrink-0 text-[10px] font-semibold tabular-nums text-slate-400"
            data-testid={`public-team-sheet-${side}-${groupKey}-count`}
          >
            {list.length}
          </span>
        </div>
        <ul className="flex w-full flex-col">
          {list.map((p) => {
            const playerStatus = status.get(p.id);
            const state = resolveRosterRowState(playerStatus);
            return (
              <li
                key={p.id}
                // `min-w-0` on the row itself (not only the name span) —
                // a flex/grid item's default min-width is its untruncated
                // content, which silently lets a long name push the row
                // wider than its track instead of ellipsising.
                //
                // Deliberately NOT reversed for `mirrored` (PMA-COCK-148
                // addendum, 2026-09-07 client screenshot): the row order is
                // number → name → indicator cluster on BOTH columns, always.
                // `mirrored` still reverses the header/label elements above
                // for xl+ crest symmetry, but reversing the player row too
                // made the away column visually read
                // [indicator][name]...[number] on a real xl+ browser — the
                // client's exact complaint. jsdom never computes flexbox
                // layout, so this can't be caught by a DOM-order test; see
                // `PublicTeamSheet.test.tsx`'s className assertion instead.
                className="flex min-w-0 items-center gap-1.5 py-[1px]"
                data-testid="public-team-sheet-player"
                data-row-state={state}
              >
                <span
                  className="w-[18px] shrink-0 rounded-[3px] text-center text-[10px] font-bold leading-[16px] tabular-nums"
                  style={{
                    backgroundColor: withAlpha(palette.primary, "38"),
                    color: palette.textOnDark,
                  }}
                >
                  {p.jersey_number || "-"}
                </span>
                <span
                  className={`min-w-0 flex-1 truncate font-condensed text-[13px] font-medium leading-tight ${NAME_CLASS_BY_STATE[state]}`}
                >
                  {formatDisplayName(p.full_name) || p.full_name}
                </span>
                {playerStatus && (
                  <>
                    <GoalMark status={playerStatus} t={t} />
                    <OwnGoalMark status={playerStatus} t={t} />
                    <CardChips status={playerStatus} t={t} />
                    <SubstitutionMark status={playerStatus} t={t} />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <section
      className="flex min-w-0 flex-col gap-2"
      data-testid={`public-team-sheet-${side}`}
      aria-label={`${team.short_name || team.name} ${t("publicSheetLineup", "lineup")}`}
    >
      <div
        className={`flex items-center gap-2 ${mirrored ? "xl:flex-row-reverse" : ""}`}
      >
        <span
          className="h-3.5 w-[3px] shrink-0 rounded-full"
          style={{ backgroundColor: palette.primary }}
          aria-hidden="true"
        />
        <span
          className="truncate font-condensed text-sm font-bold uppercase tracking-[0.12em]"
          style={{ color: palette.textOnDark }}
        >
          {team.short_name || team.name}
        </span>
      </div>
      <div
        className={`flex min-w-0 gap-3 ${mirrored ? "xl:flex-row-reverse" : ""}`}
      >
        {/* "En cancha" / "Banca" (On pitch / Bench): the split follows live
            pitch position again (`isOnPitch`, PMA-COCK-127 v1.118.0) — see
            the comment above the `onPitch`/`bench` filters for the full
            back-and-forth. Dedicated `publicSheet*` keys rather than the
            shared `starters`/`substitutes` i18n keys used elsewhere, so this
            page's copy can be tuned independently. The DOM/testid group key
            stays `"starters"`/`"substitutes"` — frozen PMA-COCK-127 contract
            (`public-team-sheet-{side}-{starters|substitutes}[-count]`) — only
            the rendered LABEL and the split criterion feeding each group
            change; a substitution test asserting these testids is
            unaffected by which column a given player currently lands in. */}
        {renderGroup("publicSheetOnPitch", "On pitch", onPitch, "starters")}
        {renderGroup("publicSheetBench", "Bench", bench, "substitutes")}
      </div>
    </section>
  );
}
